/* eslint @typescript-eslint/no-shadow: off */
import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { ReactNode, createContext } from 'react';

import { getSymbolsApi } from '@flipster/apis/v2/market';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { userRegulationBunja } from '@flipster/features/src/shared/regulation';
import { TradFiCategory } from '@flipster/features/src/shared/rwa';
import { FormattedMessage } from '@flipster/i18n';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

import { TabValueType as DiscoverTabType, MarketPageTabType, SearchTabType, Sort, TabItem } from './types';
import {
  getDefaultSortForTab,
  getDiscoverTabs,
  getProductTypeForTab,
  isDiscoverTabHidden,
  resolveSearchTab,
  resolveSearchTabs,
} from './utils';

interface DiscoverTabCursor {
  tab: DiscoverTabType;
  sort: Sort;
  page: number;
}
interface SearchCursor {
  tab: SearchTabType;
  query: string;
  page: number;
}

export const MarketPageTabContext = createContext<MarketPageTabType>('perpetual');
export const MarketPageTabScope = createScopeFromContext(MarketPageTabContext);

export type CategoryCarouselMode = 'tradfi' | 'allPerp';

export const marketPageBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const tab = bunja.use(MarketPageTabScope);
  const { isSpotOnlyRegulationAtom } = bunja.use(userRegulationBunja);
  const categoryCarouselModeAtom = atom<CategoryCarouselMode>('tradfi');
  categoryCarouselModeAtom.debugLabel = 'categoryCarouselModeAtom';
  const discoverTradfiCategoryAtom = atom<TradFiCategory>('all');
  discoverTradfiCategoryAtom.debugLabel = 'discoverTradfiCategoryAtom';
  const searchCursorAtom = atom<SearchCursor>({ tab: 'perpetual', query: '', page: 0 });
  const searchModeAtom = atom<boolean>((get) => Boolean(get(searchCursorAtom).query));
  const searchTabsAtom = atom<SearchTabType[]>((get) => resolveSearchTabs(get(isSpotOnlyRegulationAtom)));
  const searchTabAtom = atom<SearchTabType>((get) => resolveSearchTab(get(searchTabsAtom), get(searchCursorAtom).tab));
  const discoverCursorAtom = atom<DiscoverTabCursor>({
    tab: 'marketCap',
    sort: getDefaultSortForTab('marketCap', tab),
    page: 0,
  });
  const pageAtom = atom((get) => (get(searchModeAtom) ? get(searchCursorAtom).page : get(discoverCursorAtom).page));
  searchCursorAtom.debugLabel = 'searchCursorAtom';
  searchModeAtom.debugLabel = 'searchModeAtom';
  discoverCursorAtom.debugLabel = 'discoverCursorAtom';
  pageAtom.debugLabel = 'pageAtom';
  function setSearchCursorTab(tab: SearchTabType) {
    const prev = store.get(searchCursorAtom);
    if (prev.tab === tab) return;
    store.set(searchCursorAtom, { tab, query: prev.query, page: 0 });
  }
  function setSearchCursorQuery(query: string) {
    const prev = store.get(searchCursorAtom);
    if (prev.query === query) return;
    const tab = !prev.query && query ? store.get(searchTabsAtom)[0] : prev.tab;
    store.set(searchCursorAtom, { tab, query, page: 0 });
  }
  function setSearchCursorPage(page: number) {
    store.set(searchCursorAtom, { ...store.get(searchCursorAtom), page });
  }
  function setDiscoverCursorTab(discoverTab: DiscoverTabType) {
    const prev = store.get(discoverCursorAtom);
    if (prev.tab === discoverTab) return;
    store.set(discoverCursorAtom, { tab: discoverTab, sort: getDefaultSortForTab(discoverTab, tab), page: 0 });
    store.set(discoverTradfiCategoryAtom, 'all');
  }
  function setDiscoverTradfiCategory(category: TradFiCategory) {
    if (store.get(discoverTradfiCategoryAtom) === category) return;
    store.set(discoverTradfiCategoryAtom, category);
    const prev = store.get(discoverCursorAtom);
    store.set(discoverCursorAtom, { ...prev, page: 0 });
  }
  function setDiscoverCursorSort(sort: Sort) {
    const prev = store.get(discoverCursorAtom);
    store.set(discoverCursorAtom, { tab: prev.tab, sort, page: 0 });
  }
  function setDiscoverCursorPage(page: number) {
    store.set(discoverCursorAtom, { ...store.get(discoverCursorAtom), page });
  }
  function setCursorPage(page: number) {
    if (store.get(searchModeAtom)) setSearchCursorPage(page);
    else setDiscoverCursorPage(page);
  }
  return {
    tab,
    pageAtom,
    categoryCarouselModeAtom,
    discoverTradfiCategoryAtom,
    searchCursorAtom,
    searchModeAtom,
    searchTabsAtom,
    searchTabAtom,
    discoverCursorAtom,
    setSearchCursorTab,
    setSearchCursorQuery,
    setSearchCursorPage,
    setDiscoverCursorTab,
    setDiscoverTradfiCategory,
    setDiscoverCursorSort,
    setDiscoverCursorPage,
    setCursorPage,
  };
});
marketPageBunja.debugLabel = 'marketPageBunja';

export const marketSymbolBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { tab: marketTab, searchTabAtom, searchModeAtom } = bunja.use(marketPageBunja);
  const tabAtom = atom((get) => (get(searchModeAtom) ? get(searchTabAtom) : marketTab));
  const symbolsByMarketTabBaseAtom = atomWithQuery((get) => {
    const productType = getProductTypeForTab(get(tabAtom));
    return {
      queryKey: ['getSymbolsApi', productType],
      queryFn: () => getSymbolsApi({ productType }).then((res) => res.data),
    };
  });
  const isLoadingAtom = atom((get) => get(symbolsByMarketTabBaseAtom).isLoading);
  const symbolsByMarketTabAtom = atom((get) => get(symbolsByMarketTabBaseAtom).data);
  async function refetchSymbolsByMarketTab() {
    const { refetch } = store.get(symbolsByMarketTabBaseAtom);
    await refetch();
  }
  return { isLoadingAtom, symbolsByMarketTabAtom, refetchSymbolsByMarketTab };
});
marketSymbolBunja.debugLabel = 'marketSymbolBunja';

const getDiscoverTabLabel = (value: DiscoverTabType, marketTab: MarketPageTabType): ReactNode => {
  switch (value) {
    case 'favorites':
      return <FormattedMessage id='Market.r250919.Favorites' />;
    case 'tradfi':
      return <FormattedMessage id='Market.r260205.TradFi' />;
    case 'marketCap':
      return marketTab === 'spot' ? (
        <FormattedMessage id='Assets.r250401.Spot' />
      ) : (
        <FormattedMessage id='Navigation.r250401.Perpetual' />
      );
    case 'newListings':
      return <FormattedMessage id='Common.r250919.New' />;
    case 'hotTradings':
      return <FormattedMessage id='Trade.r260324.Hot' />;
    case 'absFundingRates':
      return <FormattedMessage id='Home.r260708.Funding_rates' />;
    default:
      return value;
  }
};

export const marketTabListBunja = bunja(() => {
  const { isLoggedInAtom } = bunja.use(authBunja);
  const { tab: marketTab } = bunja.use(marketPageBunja);
  const { symbolsByMarketTabAtom } = bunja.use(marketSymbolBunja);
  const tabListAtom = atom((get): TabItem[] => {
    const symbols = get(symbolsByMarketTabAtom);
    const isLoggedIn = get(isLoggedInAtom);
    return getDiscoverTabs(symbols?.layout?.components)
      .map(
        ({ value, dataRef }): TabItem => ({
          label: getDiscoverTabLabel(value, marketTab),
          value,
          isHide: isDiscoverTabHidden(value, dataRef, symbols, isLoggedIn),
        }),
      )
      .filter((item) => !item.isHide);
  });
  return { tabListAtom };
});
marketTabListBunja.debugLabel = 'marketTabListBunja';
